import * as _ from 'lodash';
import { IDateFilter, IFilter, Filter, IFilterTextMap, IFilterOption } from '../state-helpers/filters';
import { IOrderHistoryState } from '../../order-history/reducers/index';
import { IOrderHistoryResultItem } from '../../order-history/state-helpers/order-history-results';
import { IFacetResult } from '../../search-page/state-helpers/facets';
import { pushInUrlQuery, multiPushInUrlQuery } from 'react-url-query';

export function updateFilters<T>(filters: IFilter<T>[], newFilter: IFilter<T>) {
    return filters.map((filter) =>
        filter.id === newFilter.id
            ? newFilter
            : filter
    );
}

export function updateRadioSelection<T>(filter: IFilter<T>, selection: string): IFilter<T> {
    let newFilter = _.cloneDeep(filter);
    newFilter.options = filter.options.map((option) => Object.freeze(
        {
            id: option.id,
            display: option.display,
            rule: option.rule,
            selected: option.id === selection,
            count: option.count,
        }
    ));

    return newFilter;
}

// Update filters model in state based on URL
export function updateUrlFilters<T>(urlFilterList: string[], filters: IFilter<T>[]): IFilter<T>[] {
    let newFilters = _.clone(filters);

    for (var filterIndex = 0; filterIndex < filters.length; filterIndex++) {
        var filter = newFilters[filterIndex];
        for (var optionIndex = 0; optionIndex < filter.options.length; optionIndex++) {
            var option = filter.options[optionIndex];
            if (urlFilterList.indexOf(option.id) !== -1) {
                option.selected = true;
            }
            else {
                option.selected = false;
            }
        }

    }

    return newFilters;
}

export function readFacetsFromUrl<T>(urlFilterList: string[], filters: IFacetResult[]): IFacetResult[] {
    let newFilters = _.clone(filters);

    for (var currentFacet = 0; currentFacet < filters.length; currentFacet++) {
        var facet = newFilters[currentFacet];
        for (var currentOption = 0; currentOption < facet.terms.length; currentOption++) {
            var option = facet.terms[currentOption]
            if (urlFilterList.indexOf(option.displayTerm) != -1) {
                option.selected = true;
            }
            else {
                option.selected = false;
            }
        }

    }

    return newFilters;
}

export function updateUrlDateFilters(urlFilterList: string[], filters: IDateFilter[]): IDateFilter[] {
    let newFilters = _.clone(filters);

    for (var filterIndex = 0; filterIndex < filters.length; filterIndex++) {
        var filter = newFilters[filterIndex];

        if (urlFilterList.indexOf(filter.id) != -1) {
            filter.selected = true;
        } else {
            filter.selected = false;
        }
    }

    return newFilters;
}

export function setUrlFilters<T>(filters: IFilter<T>[]) {
    var filterList = filters;
    var selectedFilters: string[] = [];

    for (var currentFacet = 0; currentFacet < filters.length; currentFacet++) {
        var facet = filterList[currentFacet];
        for (var currentOption = 0; currentOption < facet.options.length; currentOption++) {
            var option = facet.options[currentOption]
            if (option.selected === true) {
                selectedFilters.push(option.id);
            }
        }
    }

    var urlFilters = JSON.stringify(selectedFilters);
    multiPushInUrlQuery({ filters: urlFilters });
}

export function setUrlFacets<T>(filters: IFacetResult[]) {
    var filterList = filters;
    var selectedFilters: string[] = [];
    var shortenedFacets = filters.filter(facetResult => facetResult.terms.some(term => term.selected))
        .map(facetResult => (
            {
                fieldName: facetResult.fieldName,
                name: facetResult.name,
                terms: facetResult.terms.filter(term => term.selected).map(term => ({ term: term.term }))
            }));

    var urlFacets = JSON.stringify(shortenedFacets);
    multiPushInUrlQuery({ facets: urlFacets });
}

export function setUrlPage(pageNumber: number) {
    pushInUrlQuery('page', pageNumber);
}

export function setUrlDateFilters(dateFilters: IDateFilter[]) {
    var dateFacet = dateFilters;
    var selectedFilters: string[] = [];

    for (var currentFacet = 0; currentFacet < dateFacet.length; currentFacet++) {
        var facet = dateFilters[currentFacet];
        if (facet.selected === true) {
            selectedFilters.push(facet.id);
        }

    }
    var urlFilters = JSON.stringify(selectedFilters);
    multiPushInUrlQuery({ dateFilters: urlFilters });
}

export function updateCheckboxSelection<T>(filter: IFilter<T>, selection: string): IFilter<T> {
    let newFilter = _.cloneDeep(filter);
    newFilter.options = filter.options.map((option) => Object.freeze(
        {
            id: option.id,
            display: option.display,
            rule: option.rule,
            selected: option.id === selection ? !option.selected : option.selected,
            count: option.count
        }
    ));

    return newFilter;
}

/**
 * Serializes the current state of the filters/search text. Used to persist the filter state in the URL bar.
 */
export function serializeFilters(filters: { id: string, selectedOptionIds: string[] }[]): string {
    return filters.reduce((acc, f) => {
        const selectedOptions = encodeURIComponent(f.selectedOptionIds.join(','));

        if (selectedOptions.length > 0) {
            acc += `&${f.id}=${selectedOptions}`;
        }

        return acc;
    }, '').slice(1);
}

export function updateDateSelection(dateFilters: IDateFilter[], value: string) {
    var updatedFilters: IDateFilter[] = dateFilters.map(dateFilter => {
        return {
            id: dateFilter.id,
            display: dateFilter.display,
            startDate: dateFilter.startDate,
            endDate: dateFilter.endDate,
            selected: value === dateFilter.id
        }
    });

    setUrlDateFilters(updatedFilters);
    return updatedFilters;
}


/// Text mapping for epi props on filters
export function getIdFromMappedValue<T>(textMap: IFilterTextMap[], filter: IFilter<T>, value: string) {
    if (textMap.length === 0) return value;

    for (var i = 0; i < textMap.length; i++) {
        if (textMap[i].options.some(option => option.display === value)) {
            let optionId = textMap[i].options.filter(option => option.display === value)[0].optionId;
            if (filter.options.some(option => option.id === optionId))
                return filter.options.filter(option => option.id === optionId)[0].id;
        }
    }

    return value;
}

export function getMappedSelectedValue<T>(textMap: IFilterTextMap[], filter: IFilter<T>) {
    let currentSelectedFilters = filter.options.filter((item) => item.selected);
    return typeof (currentSelectedFilters) != 'undefined' && currentSelectedFilters != null && currentSelectedFilters.length > 0
        ? getMappedOptionDisplayText(textMap, filter, currentSelectedFilters[0])
        : '';
}

export function getMappedSelectedValues<T>(textMap: IFilterTextMap[], filter: IFilter<T>) {
    let currentSelectedFilters = filter.options.filter((item) => item.selected);
    return (typeof (currentSelectedFilters) != 'undefined' && currentSelectedFilters != null && currentSelectedFilters.length > 0)
        ? currentSelectedFilters.map((item) => getMappedOptionDisplayText(textMap, filter, item))
        : [];
}

export function getMappedFilterDisplayText<T>(textMap: IFilterTextMap[], filter: IFilter<T>) {
    if (textMap.length === 0) return filter.display;

    let displayText = textMap.filter(text => text.filterId === filter.id);

    return typeof (displayText) != 'undefined' && displayText != null && displayText.length > 0
        ? displayText[0].display
        : filter.display;
}

export function getMappedOptionDisplayText<T>(textMap: IFilterTextMap[], filter: IFilter<T>, option: IFilterOption<T>) {
    if (textMap.length === 0) return option.display;

    let mappedText = '';

    for (var i = 0; i < textMap.length; i++) {
        if (textMap[i].options.some(textOption => textOption.optionId === option.id)) {
            let mappedOption = textMap[i].options.filter(textOption => textOption.optionId === option.id)[0];

            mappedText = mappedOption.display;
        }
    }

    return mappedText && mappedText.length > 0 ? mappedText : option.display;
}
/////////////////

// Filter out any filter that doesnt match any of the current results
export function getFilteredFilterResults<T>(filters: IFilter<T>[], results: T[]) {
    let filteredFilters: IFilter<T>[] = [];
    filteredFilters = filters.map(filter => Object.freeze({
        display: filter.display,
        id: filter.id,
        rule: filter.rule,
        template: filter.template,
        options: filter.options.filter(option => results && results.some(activity => option.rule(activity)))
    }) as IFilter<T>)
        .filter(filteredFilter => filteredFilter.options && filteredFilter.options.length > 0);
    return filteredFilters;
}